import { usePwa } from '@hooks/UsePwa';
import { useTimer } from '@hooks/UseTimer';
import { theme } from '@theme';
import { FC, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useConfig } from '../../../hooks/UseConfig';
import { useIsAdminUser } from '../../../hooks/essential-ui/UseIsAdminUser';
import { logoutRedirect } from '../../../services/auth/AuthService';
import Logo from './logo/Logo';
import SelectStorePageHeaderPanel from './select-store-page-header-panel/SelectStorePageHeaderPanel';

type AppHeaderProps = {
  isLogoDisabled?: boolean;
};

const getIpAuthorized = () => {
  return localStorage.getItem('ipAuthorized') && localStorage.getItem('ipAuthorized') === 'true'
    ? true
    : false;
};

const AppHeader: FC<AppHeaderProps> = ({ isLogoDisabled }) => {
  const [isIpAuthorized, setIsIpAuthorized] = useState<boolean>(false);

  const { isAdminUser } = useIsAdminUser();
  const { adminUrl, privateIncentiveLookUp, pwaMfaTimer } = useConfig();
  const { isPwa } = usePwa();
  const { startTimer, resetTimer, timeout } = useTimer(Number(pwaMfaTimer));

  const navigateToAdminPortal = () => window.open(adminUrl, '_blank');
  const redirectToPrivateIncentiveLookup = () => window.open(privateIncentiveLookUp, '_blank');
  const logout = () => {
    localStorage.removeItem('timerStart');
    logoutRedirect();
  };

  useEffect(() => {
    const handleContextMenu = (event: MouseEvent) => {
      event.preventDefault();
    };
    if (isPwa) {
      document.body.classList.add('disable-long-press');
      document.addEventListener('contextmenu', handleContextMenu);
    }

    const handleChangeStorage = () => {
      setIsIpAuthorized(getIpAuthorized());
    };

    window.addEventListener('storage', handleChangeStorage);

    return () => {
      window.removeEventListener('storage', handleChangeStorage);
      document.removeEventListener('contextmenu', handleContextMenu);
    };
  }, []);

  useEffect(() => {
    if (isPwa && isIpAuthorized) {
      const events = [
        'mousedown',
        'mousemove',
        'keypress',
        'keydown',
        'click',
        'scroll',
        'touchstart',
      ];
      events.forEach((event) => window.addEventListener(event, startTimer));
      startTimer(); // Start the timer initially

      return () => {
        events.forEach((event) => window.removeEventListener(event, startTimer));
      };
    } else {
      resetTimer();
      return () => {
        const events = [
          'mousedown',
          'mousemove',
          'keypress',
          'keydown',
          'click',
          'scroll',
          'touchstart',
        ];
        events.forEach((event) => window.removeEventListener(event, startTimer));
      };
    }
  }, [isIpAuthorized]);

  useEffect(() => {
    if (isPwa && timeout && localStorage.getItem('ipAuthorized') === 'false') {
      logout();
    }
  }, [timeout]);

  return (
    <div data-testid='store-select-header'>
      <Wrapper>
        <Logo $isStoreSelectHeader={true} $isLogoDisabled={isLogoDisabled} />
        <SelectStorePageHeaderPanel
          isAdminUser={isAdminUser}
          redirectToAdminPanel={navigateToAdminPortal}
          redirectToPrivateIncentiveLookup={redirectToPrivateIncentiveLookup}
          logout={logout}
        />
      </Wrapper>
    </div>
  );
};

export default AppHeader;

const Wrapper = styled.section`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 70px;
  padding: 20px;
  background: linear-gradient(
    ${(props) => props.theme.palette.appHeaderGradient.from},
    ${(props) => props.theme.palette.appHeaderGradient.to}
  );
  position: fixed;
  z-index: 1000;
  color: ${theme.palette.white};
`;
